import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Package, MessageSquare, Heart, AlertTriangle, LifeBuoy, Sparkles, ChevronRight } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { timeAgo } from "../lib/format";
import { notifLink, notifLabel, notifGroup } from "../lib/notifications";
import { Button } from "../components/ui/button";

const GROUP_META = {
  orders: { icon: Package, color: "bg-indigo-100 text-indigo-700" },
  chat: { icon: MessageSquare, color: "bg-blue-100 text-blue-700" },
  favorites: { icon: Heart, color: "bg-rose-100 text-rose-700" },
  disputes: { icon: AlertTriangle, color: "bg-amber-100 text-amber-700" },
  support: { icon: LifeBuoy, color: "bg-teal-100 text-teal-700" },
  system: { icon: Sparkles, color: "bg-slate-100 text-slate-600" },
};
const FILTERS = ["all", "orders", "chat", "favorites", "disputes", "support", "system"];

export default function NotificationsCenter() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/notifications"); setItems(data); } catch {}
  }, []);
  useEffect(() => { if (user === false) navigate("/auth"); else if (user) load(); }, [user, navigate, load]);

  const markAll = async () => { try { await api.post("/notifications/read"); load(); } catch {} };
  const markOne = async (id) => { try { await api.post(`/notifications/${id}/read`); setItems((p) => p.map((n) => n.id === id ? { ...n, read: true } : n)); } catch {} };

  const onClick = async (n) => {
    if (!n.read) markOne(n.id);
    const link = notifLink(n, user);
    if (link) navigate(link);
  };

  const filtered = filter === "all" ? items : items.filter((n) => notifGroup(n) === filter);
  const unread = items.filter((n) => !n.read).length;

  return (
    <div className="px-4 sm:px-8 py-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl flex items-center gap-2">
          <Bell className="h-6 w-6 text-[#008080]" />{t("notifCenter.title")}
          {unread > 0 && <span className="text-sm font-bold text-white bg-rose-500 rounded-full px-2 py-0.5">{unread}</span>}
        </h1>
        <Button data-testid="mark-all-read-btn" onClick={markAll} variant="outline" disabled={unread === 0} className="h-9">
          <CheckCheck className="h-4 w-4 mr-1" />{t("notifCenter.markAll")}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {FILTERS.map((f) => (
          <button key={f} data-testid={`notif-filter-${f}`} onClick={() => setFilter(f)}
            className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${filter === f ? "bg-[#008080] text-white border-[#008080]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            {t(`notifCenter.filters.${f}`)}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-20 text-slate-400">
          <Bell className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p>{t("notifications.empty")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((n) => {
            const g = notifGroup(n);
            const meta = GROUP_META[g] || GROUP_META.system;
            const Icon = meta.icon;
            const clickable = !!notifLink(n, user);
            return (
              <div key={n.id} data-testid={`notif-row-${n.type}`}
                className={`flex items-start gap-3 rounded-2xl border p-4 transition-all ${!n.read ? "bg-teal-50/40 border-teal-200" : "bg-white border-slate-200"}`}>
                <span className={`h-9 w-9 grid place-items-center rounded-xl shrink-0 ${meta.color}`}><Icon className="h-4 w-4" /></span>
                <button onClick={() => onClick(n)} disabled={!clickable}
                  className={`flex-1 min-w-0 text-left ${clickable ? "cursor-pointer" : "cursor-default"}`}>
                  <p className="text-sm text-slate-800">{notifLabel(n, t)}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">{timeAgo(n.created_at, lang)}{clickable && <ChevronRight className="h-3 w-3" />}</p>
                </button>
                {!n.read && (
                  <button data-testid={`mark-read-${n.id}`} onClick={() => markOne(n.id)} title={t("notifCenter.markRead")}
                    className="h-2.5 w-2.5 rounded-full bg-[#008080] shrink-0 mt-1 hover:scale-125 transition-transform" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
